import { useState } from 'react';
import {
  SplashOnboardingStep2ElementContentModel,
  SplashOnboardingStep2PropertiesModel,
} from '@/services/platform/api/model';
import ModuleWrapper from '../../../module/wrapper';
import { cn, ModuleProperties, SplashContext, useClientTranslation, useLocaleStore } from '@/lib/core';
import { StepIndicator } from '../step-indicator';
import { Button } from '@/components/ui/button';
import {
  requestPushNotificationPermission,
  skipPushNotifications,
} from '@/lib/notifications/pushNotifications';
import { enableAllNotificationCookies } from '@/services/platform/data/profile/notifications';

export const SplashOnboardingStep2 = (props: ModuleProperties) => {
  const content = props.content as SplashOnboardingStep2ElementContentModel | undefined;

  const translation = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);

  if (!content) return null;

  const properties = content.properties as SplashOnboardingStep2PropertiesModel;

  const ctx = props.context as SplashContext | undefined;

  const [loading, setLoading] = useState(false);

  const handleAllowNotifications = async () => {
    setLoading(true);
    await Promise.all([
      requestPushNotificationPermission(),
      enableAllNotificationCookies(locale, props?.context?.settings),
    ]);
    setLoading(false);
    ctx?.onComplete?.();
  };

  const handleSkipNotifications = () => {
    skipPushNotifications();
    ctx?.onComplete?.();
  };

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        base: cn(['step__onboarding-2', 'justify-start!', 'min-h-[calc(100vh-32px)]']),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex', 'flex-col', 'flex-1', 'justify-between', 'gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      <div className="flex flex-1 flex-col items-center justify-between gap-19 pt-13 pb-2.5">
        {/* Step indicator */}
        <StepIndicator current={props?.index + 2} total={2} />

        {/* Body text */}
        {(properties?.allowNotificationsLabel || properties?.notificationsText) && (
          <div className="flex w-full flex-col gap-12 text-center">
            {properties?.notificationsText && (
              <div className="font-pp-neue-montreal text-[14px] leading-[1.2] font-semibold tracking-[0.5px] text-white uppercase">
                {properties.notificationsText}
              </div>
            )}
            <Button
              variant="primary-solid"
              palette="white"
              size="md"
              className="button-notifications w-full leading-3.5"
              loading={loading}
              disabled={loading}
              onClick={handleAllowNotifications}
            >
              {properties?.allowNotificationsLabel || translation('App.Labels.AllowNotifications')}
            </Button>
          </div>
        )}

        {/* Bottom actions */}
        <div className="flex w-full flex-col items-center gap-6 px-6 py-4.75">
          {properties?.skipLabel && (
            <button
              type="button"
              className="button-skip font-pp-neue-montreal text-[12px] leading-3.5 font-[350] tracking-[0.5px] text-white uppercase"
              onClick={handleSkipNotifications}
            >
              {properties.skipLabel}
            </button>
          )}
        </div>
      </div>
    </ModuleWrapper>
  );
};
